import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

/**
 * Choice controls from the property and unit screens: checkbox pills, vertical option list (design/Ereignis), number stepper and wizard step chips (design/ObjektAnlegen).
 * Radio option cards: RadioCards (./choice), filter chips: ChipFilter (./process). All texts come from the caller (i18n lives in the apps).
 */
export type ListOption<V extends string> = { value: V; title: ReactNode; text?: ReactNode; disabled?: boolean };

/** Checkbox pills (design/EinheitAnlegen "Besonderheiten"). */
export function ToggleChips<V extends string>({ label, options, value, onChange }: { label: ReactNode; options: { value: V; label: ReactNode }[]; value: V[]; onChange: (v: V[]) => void }) {
  const toggle = (v: V, on: boolean) => onChange(options.map((o) => o.value).filter((x) => (x === v ? on : value.includes(x))));
  return (
    <fieldset className="toggle-chips">
      <legend>{label}</legend>
      <div className="tg-list">
        {options.map((o) => (
          <label key={o.value} className={`toggle-chip ${value.includes(o.value) ? 'checked' : ''}`}>
            <input type="checkbox" checked={value.includes(o.value)} onChange={(e) => toggle(o.value, e.target.checked)} />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Vertical list of options with title and sub line; selected item is filled petrol (design/Ereignis). */
export function ListChoice<V extends string>({ label, options, value, onChange }: { label: string; options: ListOption<V>[]; value: V; onChange: (v: V) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const enabled = options.filter((o) => !o.disabled);
  const onKey = (e: KeyboardEvent) => {
    if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    const i = enabled.findIndex((o) => o.value === value);
    const next = enabled[(i + (e.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length];
    if (!next) return;
    onChange(next.value);
    requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus());
  };
  return (
    <div className="list-choice" role="radiogroup" aria-label={label} ref={ref} onKeyDown={onKey}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} tabIndex={o.value === value ? 0 : -1} disabled={o.disabled} onClick={() => onChange(o.value)}>
          <span className="lc-title">{o.title}</span>
          {o.text && <span className="lc-sub">{o.text}</span>}
        </button>
      ))}
    </div>
  );
}

/** − n + stepper for small counts (floors, flats, people). Labels for the buttons come from the caller. */
export function NumberStepper({ label, value, onChange, min = 0, max = 99, decLabel, incLabel, labelHidden = true }: { label: string; value: number; onChange: (n: number) => void; min?: number; max?: number; decLabel: string; incLabel: string; labelHidden?: boolean }) {
  const id = useId();
  const set = (n: number) => onChange(Math.max(min, Math.min(max, Number.isFinite(n) ? Math.round(n) : min)));
  return (
    <span className="num-stepper">
      <label htmlFor={id} className={labelHidden ? 'sr-only' : 'ns-label'}>{label}</label>
      <span className="ns-box">
        <button type="button" aria-label={decLabel} disabled={value <= min} onClick={() => set(value - 1)}>−</button>
        <input id={id} inputMode="numeric" value={value} onChange={(e) => set(Number(e.target.value.replace(/\D/g, '') || min))} onFocus={(e) => e.target.select()} />
        <button type="button" aria-label={incLabel} disabled={value >= max} onClick={() => set(value + 1)}>+</button>
      </span>
    </span>
  );
}

/** Wizard steps (numbered chips): done steps are green and clickable, the current one is highlighted. */
export function StepChips({ steps, current, onSelect, label }: { steps: { key: string; label: string }[]; current: number; onSelect?: (i: number) => void; label: string }) {
  return (
    <ol className="step-chips" aria-label={label}>
      {steps.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'todo';
        const inner = <><span className="sc-num" aria-hidden>{i + 1}</span><span className="sc-label">{s.label}</span></>;
        return (
          <li key={s.key} className={`sc-${state}`} aria-current={state === 'current' ? 'step' : undefined}>
            {state === 'done' && onSelect ? <button type="button" onClick={() => onSelect(i)}>{inner}</button> : <span>{inner}</span>}
          </li>
        );
      })}
    </ol>
  );
}
